import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { getDisplayName, splitList } from '../../views/portal/utils/format.js'
import { Button } from '../button/button.js'
import { MagniferZoomIn, MagniferZoomOut, Maximize } from '../icon/solar-icons.js'
import '../portal-avatar/portal-avatar.css'
import { PortalAvatar } from '../portal-avatar/portal-avatar.js'
import { buildOrgForest, type OrgChartPerson, type OrgTreeNode, treeSize } from './org-forest.js'
import './portal-org-chart.css'

export interface PortalOrgChartLabels {
    title: (count: number) => string
    leave: string
    reports: (count: number) => string
    zoomIn: string
    zoomOut: string
    fit: string
    allTeams: string
    teamFilter: string
}

export interface PortalOrgChartProps {
    /** All people to chart (manager_id links them). */
    employees: OrgChartPerson[]
    /** Navigate to a path (e.g. `/people/42`); the overlay closes itself first. */
    onNavigate: (path: string) => void
    onClose: () => void
    labels: PortalOrgChartLabels
}

const MIN_SCALE = 0.3
const MAX_SCALE = 2
const FIT_PADDING = 0.9

interface Transform {
    x: number
    y: number
    scale: number
}

const clampScale = (s: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s))

/** One person card; navigates to the profile on click. */
const OrgCard = ({
    node,
    onNavigate,
    reportsLabel,
    activeTeam,
}: {
    node: OrgTreeNode
    onNavigate: (path: string) => void
    reportsLabel: (count: number) => string
    activeTeam: string | null
}) => {
    const { person, directReports } = node
    const name = getDisplayName(person)
    const company = splitList(person.company_slugs, ',')
        .map((c) => c.toUpperCase())
        .join(', ')
    const subParts = [company, directReports > 0 ? reportsLabel(directReports) : null].filter(
        Boolean,
    )

    const filterClass =
        activeTeam === null
            ? ''
            : splitList(person.team_names, ',').includes(activeTeam)
              ? ' portal-org-chart__card--hit'
              : ' portal-org-chart__card--dim'

    return (
        <button
            type="button"
            className={`portal-org-chart__card${filterClass}`}
            onClick={() => onNavigate(`/people/${person.id}`)}
        >
            <PortalAvatar name={name} avatarUrl={person.avatar_url} px={36} />
            <div className="portal-org-chart__card-info">
                <div className="portal-org-chart__card-name">{name}</div>
                {person.job_title && (
                    <div className="portal-org-chart__card-title">{person.job_title}</div>
                )}
                {subParts.length > 0 && (
                    <div className="portal-org-chart__card-meta">{subParts.join(' · ')}</div>
                )}
            </div>
        </button>
    )
}

/** Near-square column count for a compact grid of n cards. */
const gridColumns = (n: number): number => Math.ceil(Math.sqrt(n))

/**
 * Recursive subtree. Card sits at the bottom; reports stack above
 * (column-reverse). 2+ leaf reports (nobody under them) collapse into one
 * compact grid block instead of widening the row — a flat org grows tall,
 * not into an endless strip. Sub-leads keep branching as normal subtrees.
 */
const OrgSubtree = ({
    node,
    onNavigate,
    reportsLabel,
    activeTeam,
}: {
    node: OrgTreeNode
    onNavigate: (path: string) => void
    reportsLabel: (count: number) => string
    activeTeam: string | null
}) => {
    const leaves = node.children.filter((c) => c.children.length === 0)
    const stacked = leaves.length >= 2 ? leaves : []
    const branching =
        stacked.length > 0 ? node.children.filter((c) => c.children.length > 0) : node.children

    return (
        <div
            className={`portal-org-chart__node${node.children.length > 0 ? ' portal-org-chart__node--branch' : ''}`}
        >
            <OrgCard
                node={node}
                onNavigate={onNavigate}
                reportsLabel={reportsLabel}
                activeTeam={activeTeam}
            />
            {node.children.length > 0 && (
                <div className="portal-org-chart__children">
                    {branching.map((child) => (
                        <OrgSubtree
                            key={child.person.id}
                            node={child}
                            onNavigate={onNavigate}
                            reportsLabel={reportsLabel}
                            activeTeam={activeTeam}
                        />
                    ))}
                    {stacked.length > 0 && (
                        <div className="portal-org-chart__node">
                            <div
                                className="portal-org-chart__stack"
                                style={{
                                    gridTemplateColumns: `repeat(${gridColumns(stacked.length)}, max-content)`,
                                }}
                            >
                                {stacked.map((leaf) => (
                                    <OrgCard
                                        key={leaf.person.id}
                                        node={leaf}
                                        onNavigate={onNavigate}
                                        reportsLabel={reportsLabel}
                                        activeTeam={activeTeam}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export const PortalOrgChart = ({ employees, onNavigate, onClose, labels }: PortalOrgChartProps) => {
    const forest = buildOrgForest(employees)

    // Real hierarchies render as trees (largest first); manager-less people with
    // no reports would otherwise stretch the root row into a flat strip, so they
    // get a compact near-square grid above the trees instead.
    const trees = forest
        .filter((n) => n.children.length > 0)
        .sort((a, b) => treeSize(b) - treeSize(a))
    const loose = forest.filter((n) => n.children.length === 0)

    const teamNames = [...new Set(employees.flatMap((e) => splitList(e.team_names, ',')))].sort(
        (a, b) => a.localeCompare(b),
    )

    const viewportRef = useRef<HTMLDivElement | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const dragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null)

    const [transform, setTransform] = useState<Transform>({ x: 0, y: 0, scale: 1 })
    const [activeTeam, setActiveTeam] = useState<string | null>(null)

    const fit = () => {
        const vp = viewportRef.current
        const content = contentRef.current
        if (!vp || !content) return
        const cw = content.offsetWidth
        const ch = content.offsetHeight
        if (cw === 0 || ch === 0) return
        const scale = clampScale(Math.min(vp.clientWidth / cw, vp.clientHeight / ch) * FIT_PADDING)
        setTransform({
            scale,
            x: (vp.clientWidth - cw * scale) / 2,
            y: (vp.clientHeight - ch * scale) / 2,
        })
    }

    // Frame the whole chart once it's laid out (and whenever the set changes).
    useLayoutEffect(() => {
        fit()
    }, [employees])

    // Zoom by `factor`, keeping the canvas point under (clientX, clientY) fixed.
    // Coords are viewport-relative; omit them to zoom toward the viewport centre.
    const zoomAt = (factor: number, clientX?: number, clientY?: number) => {
        const vp = viewportRef.current
        if (!vp) return
        const rect = vp.getBoundingClientRect()
        const cx = clientX === undefined ? rect.width / 2 : clientX - rect.left
        const cy = clientY === undefined ? rect.height / 2 : clientY - rect.top
        setTransform((t) => {
            const scale = clampScale(t.scale * factor)
            const ratio = scale / t.scale
            return {
                scale,
                x: cx - ratio * (cx - t.x),
                y: cy - ratio * (cy - t.y),
            }
        })
    }

    // Wheel zoom toward the cursor. Registered natively (non-passive) so we can
    // preventDefault — React's onWheel is passive and cannot block page scroll.
    useEffect(() => {
        const vp = viewportRef.current
        if (!vp) return
        const onWheel = (e: WheelEvent) => {
            e.preventDefault()
            zoomAt(Math.pow(1.0015, -e.deltaY), e.clientX, e.clientY)
        }
        vp.addEventListener('wheel', onWheel, { passive: false })
        return () => vp.removeEventListener('wheel', onWheel)
    }, [])

    const onDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Cards handle their own (single) click → navigate; don't zoom on them.
        if ((e.target as HTMLElement).closest('.portal-org-chart__card')) return
        zoomAt(1.4, e.clientX, e.clientY)
    }

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        // Let clicks on a card through — only blank canvas drags pan.
        if ((e.target as HTMLElement).closest('.portal-org-chart__card')) return
        dragRef.current = { startX: e.clientX, startY: e.clientY, ox: transform.x, oy: transform.y }
        e.currentTarget.setPointerCapture(e.pointerId)
    }

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const d = dragRef.current
        if (!d) return
        setTransform((t) => ({
            ...t,
            x: d.ox + (e.clientX - d.startX),
            y: d.oy + (e.clientY - d.startY),
        }))
    }

    const endDrag = () => {
        dragRef.current = null
    }

    return (
        <div className="portal-org-chart">
            <div className="portal-org-chart__header">
                <h2 className="portal-org-chart__heading">{labels.title(employees.length)}</h2>
                <Button size="small" variant="secondary" text={labels.leave} onClick={onClose} />
            </div>

            {teamNames.length > 0 && (
                <div
                    className="portal-org-chart__filter"
                    role="group"
                    aria-label={labels.teamFilter}
                >
                    <button
                        type="button"
                        className={`portal-org-chart__chip${activeTeam === null ? ' portal-org-chart__chip--active' : ''}`}
                        onClick={() => setActiveTeam(null)}
                    >
                        {labels.allTeams}
                    </button>
                    {teamNames.map((team) => (
                        <button
                            key={team}
                            type="button"
                            className={`portal-org-chart__chip${activeTeam === team ? ' portal-org-chart__chip--active' : ''}`}
                            onClick={() => setActiveTeam((t) => (t === team ? null : team))}
                        >
                            {team}
                        </button>
                    ))}
                </div>
            )}

            <div
                ref={viewportRef}
                className="portal-org-chart__viewport"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerLeave={endDrag}
                onDoubleClick={onDoubleClick}
            >
                <div
                    ref={contentRef}
                    className="portal-org-chart__canvas"
                    style={{
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    }}
                >
                    {loose.length > 0 && (
                        <div
                            className="portal-org-chart__loose"
                            style={{
                                gridTemplateColumns: `repeat(${gridColumns(loose.length)}, max-content)`,
                            }}
                        >
                            {loose.map((root) => (
                                <OrgCard
                                    key={root.person.id}
                                    node={root}
                                    onNavigate={onNavigate}
                                    reportsLabel={labels.reports}
                                    activeTeam={activeTeam}
                                />
                            ))}
                        </div>
                    )}
                    {trees.length > 0 && (
                        <div className="portal-org-chart__forest">
                            {trees.map((root) => (
                                <OrgSubtree
                                    key={root.person.id}
                                    node={root}
                                    onNavigate={onNavigate}
                                    reportsLabel={labels.reports}
                                    activeTeam={activeTeam}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="portal-org-chart__controls">
                <button
                    type="button"
                    className="portal-org-chart__control"
                    aria-label={labels.zoomOut}
                    title={labels.zoomOut}
                    onClick={() => zoomAt(1 / 1.2)}
                >
                    <MagniferZoomOut size={18} />
                </button>
                <button
                    type="button"
                    className="portal-org-chart__control"
                    aria-label={labels.zoomIn}
                    title={labels.zoomIn}
                    onClick={() => zoomAt(1.2)}
                >
                    <MagniferZoomIn size={18} />
                </button>
                <button
                    type="button"
                    className="portal-org-chart__control"
                    aria-label={labels.fit}
                    title={labels.fit}
                    onClick={fit}
                >
                    <Maximize size={18} />
                </button>
            </div>
        </div>
    )
}
